import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import { absUrl } from "@/lib/seo";
import FeatureSection from "./FeatureSection";
import { featureBoundaries } from "@/content/publicFeatures";
import { trackPublicFeatureEvent } from "@/lib/publicFeatureEvents";

export default function FeaturePageTemplate({
  audience,
  eyebrow,
  title,
  intro,
  groups,
  primaryCta,
  secondaryCta,
  seoTitle,
  seoDescription,
  canonical,
}) {
  const canonicalUrl = absUrl(canonical);

  useEffect(() => {
    trackPublicFeatureEvent("public_feature_page_viewed", { audience });
  }, [audience]);

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonical={canonical}
        og={{ type: "website", title: seoTitle, description: seoDescription, url: canonicalUrl }}
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: seoTitle,
          description: seoDescription,
          url: canonicalUrl,
          isPartOf: { "@type": "WebSite", name: "ConfirmPH", url: absUrl("/") },
        }]}
      />

      <header className="relative overflow-hidden border-b border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(167,243,208,0.46),_transparent_42%),linear-gradient(135deg,#f8fbf9,#ffffff)]">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-sm text-slate-500">
            <Link to="/features" className="hover:text-emerald-800 hover:underline">Features</Link>
            <span aria-hidden="true" className="px-2">/</span>
            <span className="text-slate-700">{audience === "renter" ? "Renters" : "Businesses"}</span>
          </nav>
          <div className="mt-8 max-w-4xl">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-800">{eyebrow}</p>
            <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-slate-950 sm:text-6xl">{title}</h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-600">{intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <TrackedLink {...primaryCta} audience={audience} kind="primary" />
              <TrackedLink {...secondaryCta} audience={audience} kind="secondary" />
            </div>
          </div>
        </div>
      </header>

      <div className="sticky top-14 z-30 border-b border-slate-200 bg-white/95 backdrop-blur sm:top-16">
        <nav aria-label={`${audience} feature sections`} className="mx-auto flex max-w-7xl gap-2 overflow-x-auto px-4 py-3 sm:px-6 lg:px-8">
          {groups.map((group) => (
            <a key={group.id} href={`#${group.id}`} className="shrink-0 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-emerald-300 hover:text-emerald-900">
              {group.eyebrow.replace(/^\d+\.\s*/, "")}
            </a>
          ))}
        </nav>
      </div>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {groups.map((group, index) => (
          <FeatureSection key={group.id} group={group} index={index} page={audience} />
        ))}
      </main>

      <section className="border-y border-slate-200 bg-slate-950 text-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-20">
          <div className="lg:col-span-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">Clear boundaries</p>
            <h2 className="mt-3 font-display text-3xl font-bold">What these features do not promise.</h2>
          </div>
          <ul className="space-y-4 lg:col-span-8">
            {featureBoundaries.map((boundary) => (
              <li key={boundary} className="flex items-start gap-3 text-sm leading-7 text-slate-300">
                <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-emerald-300" aria-hidden="true" />
                <span>{boundary}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-16 text-center sm:px-6 sm:py-24">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-800">See it in context</p>
        <h2 className="mt-3 font-display text-3xl font-bold text-slate-950 sm:text-4xl">
          {audience === "renter" ? "Check the booking before you depend on it." : "Give the rental one connected operational record."}
        </h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <TrackedLink {...primaryCta} audience={audience} kind="primary" placement="footer" />
          <Link to="/features" className="inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50">
            Compare both feature sets
          </Link>
        </div>
      </section>
    </>
  );
}

function TrackedLink({ to, label, audience, kind, placement = "hero" }) {
  return (
    <Link
      to={to}
      onClick={() => trackPublicFeatureEvent("public_feature_cta_clicked", { audience, kind, placement, destination: to })}
      className={kind === "primary"
        ? "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#1B4332] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#143027]"
        : "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"}
    >
      {label} <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </Link>
  );
}
